import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings } from 'lucide-react';
import PrimaryButton from '../components/PrimaryButton';
import ZoneCard from '../components/ZoneCard';
import ZoneEmptyState from '../components/ZoneEmptyState';
import AddZoneSheet from '../components/AddZoneSheet';
import { zonesApi } from '../services/gardenApi';
import { useGardenPageTitle } from '../useGardenPageTitle';

// Home screen: a collection of the user's garden zones (front yard, veg garden, etc.).
// Each zone is a card with its best photo + plant count. Tap to open the zone detail.
export default function ZonesHomeScreen() {
  useGardenPageTitle('My garden', 'My garden');
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await zonesApi.list();
      setZones(r.zones || []);
    } catch (e) {
      setError(e.message || 'Failed to load your garden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async ({ name, sun_exposure, site_notes }) => {
    const r = await zonesApi.create({
      name,
      ...(sun_exposure ? { sun_exposure } : {}),
      ...(site_notes ? { site_notes } : {}),
    });
    setZones((prev) => [...prev, r.zone]);
    navigate(`/garden/zones/${r.zone.id}`);
  };

  if (loading) {
    return (
      <div className="px-4 py-6 text-sm text-garden-muted">Loading your garden…</div>
    );
  }

  return (
    <div className="relative">
      <header className="flex items-center justify-between px-4 pt-3 pb-2">
        <h1 className="text-lg font-semibold text-garden-ink">My garden</h1>
        <button
          className="min-h-11 min-w-11 -mr-2 rounded-full flex items-center justify-center text-garden-muted"
          aria-label="Garden settings"
          onClick={() => navigate('/garden/settings')}
        >
          <Settings size={20} />
        </button>
      </header>

      {error ? (
        <div className="mx-4 mb-3 p-3 rounded-xl bg-red-50 text-sm text-red-700">{error}</div>
      ) : null}

      {zones.length === 0 ? (
        <ZoneEmptyState onAdd={() => setAddOpen(true)} />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 px-4 pb-4">
            {zones.map((z) => (
              <ZoneCard
                key={z.id}
                zone={z}
                onClick={() => navigate(`/garden/zones/${z.id}`)}
              />
            ))}
          </div>

          <div className="px-4 pb-4">
            <PrimaryButton
              variant="secondary"
              onClick={() => setAddOpen(true)}
              className="w-full"
            >
              <Plus size={18} /> Add another area
            </PrimaryButton>
          </div>
        </>
      )}

      <AddZoneSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={handleCreate}
      />
    </div>
  );
}
